/* Tiny live clock primitives shared across Day Trading.
   - useNow(interval=1000)           → ticks every second
   - fmtTime(date, tz, opts)         → formats a date in a given IANA timezone
   - WorldClockStrip                  → compact horizontal strip for the header
*/
import { useState, useEffect } from "react";

export function useNow(interval = 1000) {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), interval);
    return () => clearInterval(id);
  }, [interval]);
  return now;
}

export function fmtTime(d, tz, { withSeconds = true, hour12 = false } = {}) {
  try {
    return d.toLocaleTimeString("en-GB", {
      timeZone: tz,
      hour: "2-digit",
      minute: "2-digit",
      ...(withSeconds ? { second: "2-digit" } : {}),
      hour12,
    });
  } catch {
    return d.toLocaleTimeString();
  }
}

const ZONES = [
  { label: "ET",    tz: "America/New_York"   },
  { label: "UK",    tz: "Europe/London"      },
  { label: "UAE",   tz: "Asia/Dubai"         },
  { label: "LOCAL", tz: Intl.DateTimeFormat().resolvedOptions().timeZone },
];

export function WorldClockStrip({ compact = true }) {
  const now = useNow(1000);
  return (
    <div
      data-testid="world-clock-strip"
      style={{
        display:"inline-flex", alignItems:"center", gap: compact ? 10 : 14,
        padding:"3px 10px", borderRadius:20,
        background:"rgba(255,255,255,0.05)",
        border:"1px solid rgba(255,255,255,0.08)",
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
      }}
    >
      {ZONES.map(z => (
        <span key={z.label} style={{ display:"inline-flex", alignItems:"center", gap:4 }}
              data-testid={`clock-${z.label.toLowerCase()}`}>
          <span style={{ fontSize:9, color:"#64748b", fontWeight:700, letterSpacing:"0.05em" }}>{z.label}</span>
          <span style={{ fontSize:11, color:"#e2e8f0", fontWeight:600 }}>{fmtTime(now, z.tz)}</span>
        </span>
      ))}
    </div>
  );
}

// ── Market phase logic ─────────────────────────────────────
// Returns { phase, nextEvent, hmsUntil, nyEt(Date) } for a given `now`.
export function computeMarketPhase(now) {
  // Convert "now" into an ET wall-clock date.
  const etStr = now.toLocaleString("en-US", { timeZone: "America/New_York" });
  const et = new Date(etStr);
  const h = et.getHours(), m = et.getMinutes(), s = et.getSeconds(), day = et.getDay();
  const mins = h * 60 + m;
  const isWeekday = day >= 1 && day <= 5;

  // Phase boundaries (ET minutes since midnight).
  const PRE_OPEN = 4 * 60;        // 4:00 AM
  const OPEN     = 9 * 60 + 30;   // 9:30 AM
  const MID      = 11 * 60;       // 11:00 AM
  const POWER    = 15 * 60;       // 3:00 PM
  const CLOSE    = 16 * 60;       // 4:00 PM
  const AH_END   = 20 * 60;       // 8:00 PM

  // Build a helper that returns seconds until a given ET minute today,
  // adjusted for non-trading days.
  function secondsUntilToday(targetMins) {
    const secsNow = mins * 60 + s;
    return (targetMins * 60) - secsNow;
  }

  function nextWeekdayDelta(fromDay) {
    // Returns days to next Mon if today is Sat(6)/Sun(0), else 1.
    if (fromDay === 5) return 3;      // Fri → Mon
    if (fromDay === 6) return 2;      // Sat → Mon
    if (fromDay === 0) return 1;      // Sun → Mon
    return 1;
  }

  function fmtHms(secs) {
    const total = Math.max(0, Math.floor(secs));
    const hh = Math.floor(total / 3600);
    const mm = Math.floor((total % 3600) / 60);
    const ss = total % 60;
    return { hh, mm, ss, str: `${hh}h ${mm}m ${ss}s` };
  }

  let phase, nextLabel, secsUntilNext;

  if (!isWeekday) {
    phase = "CLOSED_WEEKEND";
    // Seconds until next Monday 9:30 AM ET
    const daysToMon = nextWeekdayDelta(day);
    const secsToday = (24 * 3600) - (mins * 60 + s);
    secsUntilNext = secsToday + (daysToMon - 1) * 24 * 3600 + OPEN * 60;
    nextLabel = "NYSE Opens Monday 9:30 AM ET";
  } else if (mins < PRE_OPEN) {
    phase = "OVERNIGHT";
    secsUntilNext = secondsUntilToday(OPEN);
    nextLabel = "NYSE Opens 9:30 AM ET";
  } else if (mins < OPEN) {
    phase = "PREMARKET";
    secsUntilNext = secondsUntilToday(OPEN);
    nextLabel = "NYSE Opens 9:30 AM ET";
  } else if (mins < MID) {
    phase = "OPEN";
    secsUntilNext = secondsUntilToday(CLOSE);
    nextLabel = "NYSE Closes 4:00 PM ET";
  } else if (mins < POWER) {
    phase = "MID";
    secsUntilNext = secondsUntilToday(POWER);
    nextLabel = "Power Hour starts 3:00 PM ET";
  } else if (mins < CLOSE) {
    phase = "POWER";
    secsUntilNext = secondsUntilToday(CLOSE);
    nextLabel = "Power Hour ends 4:00 PM ET";
  } else if (mins < AH_END) {
    phase = "AFTERHOURS";
    // Next open: if Friday after 4pm → Monday, else tomorrow.
    if (day === 5) {
      const secsToday = (24 * 3600) - (mins * 60 + s);
      secsUntilNext = secsToday + 2 * 24 * 3600 + OPEN * 60;
      nextLabel = "NYSE Opens Monday 9:30 AM ET";
    } else {
      const secsToday = (24 * 3600) - (mins * 60 + s);
      secsUntilNext = secsToday + OPEN * 60;
      nextLabel = "NYSE Opens tomorrow 9:30 AM ET";
    }
  } else {
    phase = "CLOSED";
    if (day === 5) {
      const secsToday = (24 * 3600) - (mins * 60 + s);
      secsUntilNext = secsToday + 2 * 24 * 3600 + OPEN * 60;
      nextLabel = "NYSE Opens Monday 9:30 AM ET";
    } else {
      const secsToday = (24 * 3600) - (mins * 60 + s);
      secsUntilNext = secsToday + OPEN * 60;
      nextLabel = "NYSE Opens tomorrow 9:30 AM ET";
    }
  }

  return { phase, nextLabel, secsUntilNext, hms: fmtHms(secsUntilNext), et, etMins: mins };
}
